import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { AppleSignInButton } from "@/components/AppleSignInButton";
import { authApi } from "@/lib/api";

export default function Login() {
  const { login, completeTwoStep, loginWithGoogle, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next");
  const from = location.state?.from || (nextParam && nextParam.startsWith("/") ? nextParam : "/marketplace");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await login(email.trim(), password);
      if (res?.two_step) { setChallenge(res.challenge); return; }
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.message || "Sign in failed");
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    try {
      await completeTwoStep(challenge.challenge_token, code.trim(), trust);
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.message || "That code isn't right");
    }
  };

  const resend = async (via) => {
    try {
      const r = await authApi.loginResend(challenge.challenge_token, via);
      setChallenge({ ...challenge, ...r });
      toast.success(via === "email" ? "We've emailed you a code" : "Sent a new code");
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (challenge) {
    return (
      <div className="fable-mesh flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-xl">
          <div className="mb-6 text-center">
            <Logo className="text-3xl" />
            <h1 className="mt-4 font-serif-display text-2xl font-bold">Enter your code</h1>
            <p className="mt-1 text-sm text-muted-foreground">We sent a 6-digit code to {challenge.hint || (challenge.channel === "sms" ? "your phone" : "your email")}.</p>
          </div>
          <form onSubmit={submitCode} className="space-y-4">
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoFocus placeholder="123456" className="text-center text-xl tracking-[0.4em]" data-testid="login-code" />
            <label className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} /> Trust this browser for 30 days</label>
            <Button type="submit" className="w-full rounded-full" disabled={loading || code.length < 6} data-testid="login-code-submit">{loading ? "Checking…" : "Sign in"}</Button>
          </form>
          <div className="mt-5 flex justify-between text-sm">
            <button type="button" className="font-semibold text-primary hover:underline" onClick={() => resend()}>Send a new code</button>
            {challenge.channel === "sms" && challenge.email_fallback && <button type="button" className="text-muted-foreground hover:underline" onClick={() => resend("email")}>No signal? Email me instead</button>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fable-mesh flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-xl">
        <div className="mb-6 text-center">
          <Logo className="text-3xl" />
          <h1 className="mt-4 font-serif-display text-2xl font-bold">Welcome back</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to book across all six worlds.</p>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full gap-2 rounded-full"
          onClick={loginWithGoogle}
          data-testid="google-signin"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
          Continue with Google
        </Button>
        <AppleSignInButton />

        <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="mt-1.5"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="mt-1.5"
              placeholder="••••••••"
            />
            <Link to="/forgot-password" className="mt-1.5 inline-block text-xs text-primary hover:underline" data-testid="login-forgot">Forgot password?</Link>
          </div>
          <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          New to Fable +?{" "}
          <Link to={nextParam && nextParam.startsWith("/") ? "/register?next=" + encodeURIComponent(nextParam) : "/register"} className="font-semibold text-primary hover:underline">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
